/**
 * Data of the contract detail page (`/contrats/:idNpol?id_per=…`): the seven
 * VEOS calls fired eagerly and in parallel (`useQueries`), each surfaced
 * independently so one failing tab never blanks the others:
 * FIC_POL, D_FIC_ASS (only when `id_per` is known — `WS2_EA_FIC_POL` does
 * not return the insured id), TAB_QUI_POL, TAB_GAR_POL, TAB_POL_SIN,
 * TAB_POL_PMQ, TAB_DOCS_POL.
 */

import { useQueries, type UseQueryResult } from '@tanstack/react-query';

import {
  contractQueryKeys,
  contractsService,
  type ContractDetail,
  type ContractInsured,
  type ContractPremium,
} from '../../api/contractsService';
import { ERP_REQUEST_FAILED_MESSAGE } from '../../api/erpProxy';
import type { ClaimTableRow } from '../shared/claimsTableConfig';
import type { DocumentItem } from '../shared/documentItems';
import type { GuaranteeTableRow } from '../shared/guaranteesTableConfig';
import type { PieceItem } from '../shared/pieceItems';

/** The state of one section of the page. */
export interface ContractSection<T> {
  data: T | undefined;
  loading: boolean;
  /** Error message, `null` when the call succeeded / is pending. */
  error: string | null;
}

export interface ContractDetailData {
  detail: ContractSection<ContractDetail | null>;
  /** `enabled === false` when there is no `id_per` (the call is not made). */
  insured: ContractSection<ContractInsured | null> & { enabled: boolean };
  premiums: ContractSection<ContractPremium[]>;
  guarantees: ContractSection<GuaranteeTableRow[]>;
  claims: ContractSection<ClaimTableRow[]>;
  pieces: ContractSection<PieceItem[]>;
  documents: ContractSection<DocumentItem[]>;
}

function errorMessage(error: unknown): string | null {
  if (error === null || error === undefined) return null;
  if (error instanceof Error && error.message !== '') return error.message;
  return typeof error === 'string' && error !== '' ? error : ERP_REQUEST_FAILED_MESSAGE;
}

function toSection<T>(result: UseQueryResult<T>): ContractSection<T> {
  return {
    data: result.data,
    loading: result.isPending && result.fetchStatus !== 'idle',
    error: errorMessage(result.error),
  };
}

/** Fire the seven calls of the contract detail page. */
export function useContractDetail(
  registrySlug: string | null,
  idNpol: string,
  idPer: string | null,
): ContractDetailData {
  const enabled = Boolean(registrySlug) && idNpol !== '';
  const insuredEnabled = enabled && idPer !== null && idPer !== '';
  const key = (part: string) => contractQueryKeys.detail(registrySlug, idNpol, part);

  const [detail, insured, premiums, guarantees, claims, pieces, documents] = useQueries({
    queries: [
      {
        queryKey: key('detail'),
        queryFn: () => contractsService.fetchContract(registrySlug, idNpol),
        enabled,
      },
      {
        queryKey: [...key('insured'), idPer],
        queryFn: () => contractsService.fetchInsured(registrySlug, idNpol, idPer ?? ''),
        enabled: insuredEnabled,
      },
      {
        queryKey: key('premiums'),
        queryFn: () => contractsService.fetchContractPremiums(registrySlug, idNpol),
        enabled,
      },
      {
        queryKey: key('guarantees'),
        queryFn: () => contractsService.fetchContractGuarantees(registrySlug, idNpol),
        enabled,
      },
      {
        queryKey: key('claims'),
        queryFn: () => contractsService.fetchContractClaims(registrySlug, idNpol),
        enabled,
      },
      {
        queryKey: key('pieces'),
        queryFn: () => contractsService.fetchContractPieces(registrySlug, idNpol),
        enabled,
      },
      {
        queryKey: key('documents'),
        queryFn: () => contractsService.fetchContractDocuments(registrySlug, idNpol),
        enabled,
      },
    ],
  });

  return {
    detail: toSection(detail),
    insured: { ...toSection(insured), enabled: insuredEnabled },
    premiums: toSection(premiums),
    guarantees: toSection(guarantees),
    claims: toSection(claims),
    pieces: toSection(pieces),
    documents: toSection(documents),
  };
}
